/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 10/27/2023
 * for userx-support
 */

import React, {useEffect, useState} from 'react';
import TypeAssignments from './_ui/formElements/TypeAssignments';
import AdminMessaging from "./_ui/AdminMessaging";
import useDefaultsManager from '../_hooks/useDefaultsManager';
import CRM from "./CRM";
import {useNavigate, useParams} from "react-router";
import AdminInvoices from "./AdminInvoices";
import AdminAgents from "./_ui/AdminAgents";
import AdminCompanies from "./_ui/AdminCompanies";
import WebsiteReviewManager from "./admin/WebsiteReviewManager";

const AdminSettings = () => {
    const navigate = useNavigate();
    const params = useParams();
    const groups = useDefaultsManager('group-defaults');
    const priorities = useDefaultsManager('priority-defaults');
    const status = useDefaultsManager('status-defaults');
    const types = useDefaultsManager('type-defaults');
    const modes = useDefaultsManager('mode-defaults');
    const services = useDefaultsManager('service-defaults');
    const activeTab = params.tab || 'crm';
    const tabs = [
        { id: 'crm', label: 'CRM', classes: '' },
        { id: 'agents', label: 'Agents', classes: '' },
        { id: 'companies', label: 'Companies', classes: '' },
        { id: 'invoices', label: 'Invoices', classes: '' },
        { id: 'reviews', label: 'Website Reviews', classes: '' },
        { id: 'assignments', label: 'Assignments', classes: '' },
        { id: 'defaults', label: 'Defaults', classes: '' },
    ];

    useEffect(() => {
        groups.fetchDefaults();
        priorities.fetchDefaults();
        status.fetchDefaults();
        types.fetchDefaults();
        modes.fetchDefaults();
        services.fetchDefaults();
    }, []);

    return (
        <div className="component-container d-flex flex-column h-100">
            <div className="flex-grow-1 align-items-stretch p-3 m-3">
                <h2>Settings</h2>
                <ul className="nav nav-tabs" id="myTab" role="tablist">
                    {tabs.map(tab => (
                        <li className={`nav-item ${tab.id === 'delete' ? `flex-grow-1 d-flex justify-content-end` : null}`}
                            key={tab.id} role="presentation"
                        >
                            <button
                                className={`nav-link ${tab.classes} ${activeTab === tab.id ? 'active' : ''}`}
                                type="button"
                                onClick={() => navigate(`/admins/settings/${tab.id}`)}
                            >
                                {tab.label}
                            </button>
                        </li>
                    ))}
                </ul>
                <div className="tab-content position-relative" id="settingsTabContent">

                    <div className={`position-absolute w-100 p-3 bg-white tab-pane fade ${activeTab==='crm' ? 'active show' : null}`}
                         id="messaging"
                         role="tabpanel"
                         style={{border: '1px solid #ddd'}}>
                        <CRM/>
                    </div>
                    <div className={`position-absolute w-100 p-3 bg-white tab-pane fade ${activeTab==='agents' ? 'active show' : null}`}
                         id="agents"
                         role="tabpanel"
                         style={{border: '1px solid #ddd'}}>
                        <AdminAgents/>
                    </div>
                    <div className={`position-absolute w-100 p-3 bg-white tab-pane fade ${activeTab==='companies' ? 'active show' : null}`}
                         id="companies"
                         role="tabpanel"
                         style={{border: '1px solid #ddd'}}>
                        <AdminCompanies/>
                    </div>
                    <div className={`position-absolute w-100 p-3 bg-white tab-pane fade ${activeTab==='invoices' ? 'active show' : null}`}
                         id="invoices"
                         role="tabpanel"
                         style={{border: '1px solid #ddd'}}>
                        <AdminInvoices/>
                    </div>
                    <div className={`position-absolute w-100 p-3 bg-white tab-pane fade ${activeTab==='reviews' ? 'active show' : null}`}
                         id="reviews"
                         role="tabpanel"
                         style={{border: '1px solid #ddd'}}>
                        <WebsiteReviewManager/>
                    </div>
                    <div className={`position-absolute w-100 p-3 bg-white tab-pane fade ${activeTab==='assignments' ? 'active show' : null}`}
                         id="assignments" role="tabpanel"
                         style={{border: '1px solid #ddd'}}>
                        <TypeAssignments/>
                    </div>
                    <div
                        className={`position-absolute w-100 p-3 bg-white tab-pane fade ${activeTab==='defaults' ? 'active show' : null}`}
                        id="defaults" role="tabpanel" style={{border: '1px solid #ddd'}}>
                        <h3>Contact/Client Options</h3>
                        <div className='w-100 d-flex justify-content-between gap-5 pb-3 mb-3'>
                            <div className={`w-33`}>
                                <h4>Groups</h4>
                                {groups.defaults.map((item) => (
                                    <div key={item.id} className="text-nowrap d-flex align-items-center mb-1">
                                        <span className="me-2">{item.id}</span>
                                        <span className="text-capitalize me-auto">{item.name}</span>
                                        <a
                                            className="text-danger small text-uppercase"
                                            onClick={() => groups.removeItem(item.id)}
                                            style={{cursor: 'pointer'}}
                                        >
                                            Delete
                                        </a>
                                    </div>
                                ))}

                                <form
                                    className="d-flex mt-3 mb-2"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (groups.newDefault.id && groups.newDefault.name) {
                                            groups.addItem(groups.newDefault);
                                            groups.setNewDefault({id: '', name: ''});
                                            groups.newDefaultRef.current?.focus();
                                        }
                                    }}>
                                    <input
                                        ref={groups.newDefaultRef}
                                        type="text"
                                        className="form-control me-2"
                                        style={{width: '50px'}}
                                        placeholder="ID"
                                        value={groups.newDefault.id}
                                        onChange={(e) =>
                                            groups.setNewDefault(prev => ({
                                                ...prev,
                                                id: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="form-control me-2 flex-grow-1"
                                        placeholder="New group name"
                                        value={groups.newDefault.name}
                                        onChange={(e) =>
                                            groups.setNewDefault(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type={'submit'}
                                        className="btn btn-primary"
                                        disabled={(groups.newDefault.id && !groups.newDefault.id.trim())
                                            || (groups.newDefault.name && !groups.newDefault.name.trim())}
                                        value={'Add'}
                                    />
                                </form>
                                <button className={'btn btn-success d-block ms-auto me-0'}
                                        onClick={() => groups.saveDefaults()}
                                >Save Default Groups {groups.saved ? <i className={'bi bi-check'}/> : null}
                                </button>
                            </div>
                            <div className={`w-33`}>
                                <h4>Client Modes</h4>
                                {modes.defaults.map((item) => (
                                    <div key={item.id} className="text-nowrap d-flex align-items-center mb-1">
                                        <span className="me-2">{item.id}</span>
                                        <span className="text-capitalize me-auto">{item.name}</span>
                                        <a
                                            className="text-danger small text-uppercase"
                                            onClick={() => modes.removeItem(item.id)}
                                            style={{cursor: 'pointer'}}
                                        >
                                            Delete
                                        </a>
                                    </div>
                                ))}

                                <form
                                    className="d-flex mt-3 mb-2"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (modes.newDefault.id && modes.newDefault.name) {
                                            modes.addItem(modes.newDefault);
                                            modes.setNewDefault({id: '', name: ''});
                                            modes.newDefaultRef.current?.focus();
                                        }
                                    }}>
                                    <input
                                        ref={modes.newDefaultRef}
                                        type="text"
                                        className="form-control me-2"
                                        style={{width: '50px'}}
                                        placeholder="ID"
                                        value={modes.newDefault.id}
                                        onChange={(e) =>
                                            modes.setNewDefault(prev => ({
                                                ...prev,
                                                id: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="form-control me-2 flex-grow-1"
                                        placeholder="New mode name"
                                        value={modes.newDefault.name}
                                        onChange={(e) =>
                                            modes.setNewDefault(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type={'submit'}
                                        className="btn btn-primary"
                                        disabled={(modes.newDefault.id && !modes.newDefault.id.trim())
                                            || (modes.newDefault.name && !modes.newDefault.name.trim())}
                                        value={'Add'}
                                    />
                                </form>
                                <div className={'d-flex justify-content-end'}>
                                    <button className={'btn btn-success d-block ms-auto me-0'}
                                            onClick={() => modes.saveDefaults()}
                                    >Save Default Client Modes {modes.saved ? <i className={'bi bi-check'}/> : null}
                                    </button>
                                </div>
                            </div>
                            <div className={`w-33`}>
                                <h4>Client Services</h4>
                                {services.defaults.map((item) => (
                                    <div key={item.id} className="text-nowrap d-flex align-items-center mb-1">
                                        <span className="me-2">{item.id}</span>
                                        <span className="text-capitalize me-auto">{item.name}</span>
                                        <a
                                            className="text-danger small text-uppercase"
                                            onClick={() => modes.removeItem(item.id)}
                                            style={{cursor: 'pointer'}}
                                        >
                                            Delete
                                        </a>
                                    </div>
                                ))}

                                <form
                                    className="d-flex mt-3 mb-2"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (services.newDefault.id && services.newDefault.name) {
                                            services.addItem(services.newDefault);
                                            services.setNewDefault({id: '', name: ''});
                                            services.newDefaultRef.current?.focus();
                                        }
                                    }}>
                                    <input
                                        ref={services.newDefaultRef}
                                        type="text"
                                        className="form-control me-2"
                                        style={{width: '50px'}}
                                        placeholder="ID"
                                        value={services.newDefault.id}
                                        onChange={(e) =>
                                            services.setNewDefault(prev => ({
                                                ...prev,
                                                id: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="form-control me-2 flex-grow-1"
                                        placeholder="New mode name"
                                        value={services.newDefault.name}
                                        onChange={(e) =>
                                            services.setNewDefault(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type={'submit'}
                                        className="btn btn-primary"
                                        disabled={(services.newDefault.id && !services.newDefault.id.trim())
                                            || (services.newDefault.name && !services.newDefault.name.trim())}
                                        value={'Add'}
                                    />
                                </form>
                                <div className={'d-flex justify-content-end'}>
                                    <button className={'btn btn-success d-block ms-auto me-0'}
                                            onClick={() => services.saveDefaults()}
                                    >Save Default Client Modes {services.saved ? <i className={'bi bi-check'}/> : null}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <h3 className='w-100'>Ticket Options</h3>
                        <div className='w-100 d-flex justify-content-between gap-5 pb-3 mb-3'>
                            <div className='w-33'>
                                <h4>Priorities</h4>
                                {priorities.defaults.map((item) => (
                                    <div key={item.id} className="text-nowrap d-flex align-items-center mb-1">
                                        <span className="me-2">{item.id}</span>
                                        <span className={`d-block bg-${item.color} me-2`}
                                              style={{width: '1em', height: '1em'}}></span>
                                        <span className="text-capitalize me-auto">{item.name}</span>
                                        <a
                                            className="text-danger small text-uppercase"
                                            onClick={() => priorities.removeItem(item.id)}
                                            style={{cursor: 'pointer'}}
                                        >
                                            Delete
                                        </a>
                                    </div>
                                ))}

                                <form
                                    className="d-flex mb-2 mt-2"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (priorities.newDefault.id && priorities.newDefault.name) {
                                            priorities.addItem(priorities.newDefault);
                                            priorities.setNewDefault({id: '', name: '', color: ''});
                                            priorities.newDefaultRef.current?.focus();
                                        }
                                    }}>
                                    <input
                                        ref={priorities.newDefaultRef}
                                        type="text"
                                        className="form-control me-2"
                                        style={{width: '50px'}}
                                        placeholder="ID"
                                        value={priorities.newDefault.id}
                                        onChange={(e) =>
                                            priorities.setNewDefault(prev => ({
                                                ...prev,
                                                id: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="form-control me-2 flex-grow-1"
                                        placeholder="label"
                                        value={priorities.newDefault.name}
                                        onChange={(e) =>
                                            priorities.setNewDefault(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="form-control me-2"
                                        style={{width: '100px'}}
                                        placeholder="color"
                                        value={priorities.newDefault.color}
                                        onChange={(e) =>
                                            priorities.setNewDefault(prev => ({
                                                ...prev,
                                                color: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type={'submit'}
                                        className="btn btn-primary"
                                        disabled={(priorities.newDefault.id && !priorities.newDefault.id.trim())
                                            || (priorities.newDefault.name && !priorities.newDefault.name.trim())}
                                        value={'Add'}
                                    />
                                </form>
                                <button className={'btn btn-success d-block ms-auto me-0'}
                                        onClick={() => priorities.saveDefaults()}
                                >Save Default Priorities {priorities.saved ? <i className={'bi bi-check'}/> : null}
                                </button>
                            </div>
                            <div className='w-33'>
                                <h4>Ticket Types</h4>
                                {types.defaults.map((item) => (
                                    <div key={item.id} className="text-nowrap d-flex align-items-center mb-1">
                                        <span className="me-2">{item.id}</span>
                                        <span className="text-capitalize me-auto">{item.name}</span>
                                        <a
                                            className="text-danger small text-uppercase"
                                            onClick={() => types.removeItem(item.id)}
                                            style={{cursor: 'pointer'}}
                                        >
                                            Delete
                                        </a>
                                    </div>
                                ))}

                                <form
                                    className="d-flex mt-3 mb-2"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (types.newDefault.id && types.newDefault.name) {
                                            types.addItem(types.newDefault);
                                            types.setNewDefault({id: '', name: ''});
                                            types.newDefaultRef.current?.focus();
                                        }
                                    }}>
                                    <input
                                        ref={types.newDefaultRef}
                                        type="text"
                                        className="form-control me-2"
                                        style={{width: '50px'}}
                                        placeholder="ID"
                                        value={types.newDefault.id}
                                        onChange={(e) =>
                                            types.setNewDefault(prev => ({
                                                ...prev,
                                                id: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="form-control me-2 flex-grow-1"
                                        placeholder="New type name"
                                        value={types.newDefault.name}
                                        onChange={(e) =>
                                            types.setNewDefault(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type={'submit'}
                                        className="btn btn-primary"
                                        disabled={(types.newDefault.id && !types.newDefault.id.trim())
                                            || (types.newDefault.name && !types.newDefault.name.trim())}
                                        value={'Add'}
                                    />
                                </form>
                                <button className={'btn btn-success d-block ms-auto me-0'}
                                        onClick={() => types.saveDefaults()}

                                >Save Default Ticket Types {types.saved ? <i className={'bi bi-check'}/> : null}
                                </button>
                            </div>
                            <div className='w-33'>
                                <h4>Status Types</h4>
                                {status.defaults.map((item) => (
                                    <div key={item.id} className="text-nowrap d-flex align-items-center mb-1">
                                        <span className="me-2">{item.id}</span>
                                        <span className="text-capitalize me-auto">{item.name}</span>
                                        <a
                                            className="text-danger small text-uppercase"
                                            onClick={() => status.removeItem(item.id)}
                                            style={{cursor: 'pointer'}}
                                        >
                                            Delete
                                        </a>
                                    </div>
                                ))}

                                <form
                                    className="d-flex mt-3 mb-2"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (status.newDefault.id && status.newDefault.name) {
                                            status.addItem(status.newDefault);
                                            status.setNewDefault({id: '', name: ''});
                                            status.newDefaultRef.current?.focus();
                                        }
                                    }}>
                                    <input
                                        ref={status.newDefaultRef}
                                        type="text"
                                        className="form-control me-2"
                                        style={{width: '50px'}}
                                        placeholder="ID"
                                        value={status.newDefault.id}
                                        onChange={(e) =>
                                            status.setNewDefault(prev => ({
                                                ...prev,
                                                id: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="form-control me-2 flex-grow-1"
                                        placeholder="New status name"
                                        value={status.newDefault.name}
                                        onChange={(e) =>
                                            status.setNewDefault(prev => ({
                                                ...prev,
                                                name: e.target.value
                                            }))
                                        }
                                    />
                                    <input
                                        type={'submit'}
                                        className="btn btn-primary"
                                        disabled={(status.newDefault.id && !status.newDefault.id.trim())
                                            || (status.newDefault.name && !status.newDefault.name.trim())}
                                        value={'Add'}
                                    />
                                </form>
                                <button className={'btn btn-success d-block ms-auto me-0'}
                                        onClick={() => status.saveDefaults()}
                                >Save Status Types {status.saved ? <i className={'bi bi-check'}/> : null}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminSettings;
